import {Box, Button, Collapse, Divider, Stack, Typography, useTheme} from '@mui/material';
import {QueryParamsEnum} from 'presentation/constants';
import Icon from 'presentation/icons';
import {getStepPosition} from 'presentation/modules/booking/shared/utils/getStepPosition';
import {useQueryParams} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {formatText} from 'presentation/utils';
import React, {useState} from 'react';

import {StyledStack} from './styles';

import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import type {SummaryDistrictSectionProps} from './types';
import {useSummaryChangeDrawerStore} from 'presentation/modules/booking/shared/hooks/useBookingSummary/store/useSummaryChangeDrawerStore';

const {seeMore, seeLess, change, districts} = translations['pt-br'].commons;

export function SummaryDistrictSection({
	flowType,
	dataTagId,
}: Readonly<SummaryDistrictSectionProps>) {
	const {palette} = useTheme();
	const {clearSearch, setSearchParams} = useQueryParams();
	const [expandedState, setExpandedState] = useState(false);
	const setActiveBookingFlowStep = useBookingFlowStepsStore(state => state.setBookingStep);
	const bookingData = useBookingStore(state => state.bookingData);
	const setOpenDrawerState = useSummaryChangeDrawerStore(state => state.setSummaryChangeDrawer);

	const citiesNames = bookingData?.citiesNames;
	const citiesList = citiesNames?.split(',').map(city => formatText(city)) ?? [];
	const allCitiesAreEqual = new Set(citiesList).size === 1;
	const districtsName = bookingData?.districtName;
	const districtsList = districtsName?.split(',').map(district => formatText(district)) ?? [];
	const districtsListLength = districtsList.length;
	const listHasMoreThanTwoDistricts = districtsListLength > 2;

	const districtsFormatted = citiesList?.map((city, index) => ({
		city,
		district: districtsList[index],
	}));

	const {DISTRICT_STEP} = getStepPosition({
		flowType,
		isManualLocation: bookingData.isManualLocation,
	});

	const handleExpandClick = () => {
		setExpandedState(prev => !prev);
	};

	const handleAction = () => {
		clearSearch();
		setOpenDrawerState(false);
		setSearchParams(
			prev => {
				prev.delete(QueryParamsEnum.LOCALITY_DISTANCE);
				prev.delete(QueryParamsEnum.DISABILITY);
				prev.delete(QueryParamsEnum.INCLUDE_NEIGHBORHOOD);
				return prev;
			},
			{replace: true},
		);
		setActiveBookingFlowStep(DISTRICT_STEP);
	};

	return (
		<StyledStack>
			<Divider sx={{mb: 1.5, mt: -1.5}} />
			<Stack flexDirection="row" justifyContent="space-between" alignItems="center">
				<Typography variant="subheader">{districts}</Typography>
				<Button variant="text" size="small" onClick={handleAction} data-tag-id={dataTagId}>
					{change}
				</Button>
			</Stack>
			<Stack my={1.5} direction={'row'} gap={2} alignItems={'flex-start'}>
				<Icon name={'task-alt-filled'} color={palette.primary.main} size={20} />
				<Stack gap={0.5}>
					{districtsFormatted.slice(0, 2).map((distr, index) => (
						<Stack key={index}>
							<Typography>{distr.district}</Typography>
							{allCitiesAreEqual ? null : (
								<Typography variant="caption" color="text.secondary">
									{distr.city}
								</Typography>
							)}
						</Stack>
					))}

					{listHasMoreThanTwoDistricts && !expandedState ? (
						<Typography>+{districtsListLength - 2}</Typography>
					) : null}

					<Collapse in={expandedState}>
						<Stack gap={0.5}>
							{districtsFormatted.slice(2).map((dist, index) => (
								<Stack key={index}>
									<Typography>{dist.district}</Typography>
									{allCitiesAreEqual ? null : (
										<Typography variant="caption" color="text.secondary">
											{dist.city}
										</Typography>
									)}
								</Stack>
							))}
						</Stack>
					</Collapse>
					{listHasMoreThanTwoDistricts ? (
						<Box mt={expandedState ? 0.5 : 0}>
							<Button
								size="small"
								onClick={handleExpandClick}
								variant="text"
								sx={{
									p: 0,
									minWidth: 0,
									textDecoration: 'underline',
									'&:hover': {
										textDecoration: 'underline',
									},
								}}>
								{expandedState ? `${seeLess}` : `${seeMore}`}
							</Button>
						</Box>
					) : null}
				</Stack>
			</Stack>
		</StyledStack>
	);
}
